/**
 * Fendertex sizing engine.
 *
 * Pure, framework-free module. Exposes a single `recommend(boatLength, freeboard)`
 * function that maps two physical inputs to a fender recommendation, per
 * fender-calculator-engine-spec.md.
 *
 *   boatLength: metres (5–200)
 *   freeboard:  centimetres (40–400)
 */

const CYLINDRICAL = [
  { model: 'C62',  diameterCm: 19, lengthCm: 60  },
  { model: 'C73',  diameterCm: 25, lengthCm: 70  },
  { model: 'C84',  diameterCm: 30, lengthCm: 80  },
  { model: 'C104', diameterCm: 34, lengthCm: 100 },
  { model: 'C124', diameterCm: 36, lengthCm: 120 },
  { model: 'C145', diameterCm: 49, lengthCm: 140 },
  { model: 'C175', diameterCm: 56, lengthCm: 170 },
  { model: 'C207', diameterCm: 70, lengthCm: 200 },
  { model: 'C248', diameterCm: 95, lengthCm: 240 },
];

const SPHERICAL = [
  { model: 'S60',  dimsCm: [55, 52, 41]  },
  { model: 'S70',  dimsCm: [65, 62, 50]  },
  { model: 'S80',  dimsCm: [74, 67, 55]  },
  { model: 'S100', dimsCm: [86, 90, 70]  },
  { model: 'S120', dimsCm: [98, 122, 85] },
];

const MAX_FREEBOARD_CM = 360;
const MAX_BOAT_LENGTH_M = 30;

function quantityFromBoatLength(boatLength) {
  if (boatLength > MAX_BOAT_LENGTH_M) return null;
  if (boatLength >= 20) return { cylindrical: 10, spherical: 2 };
  if (boatLength >= 16) return { cylindrical: 8,  spherical: 2 };
  if (boatLength > 9)   return { cylindrical: 8,  spherical: 0 };  // 0–9 m inclusive → 6
  return                       { cylindrical: 6,  spherical: 0 };
}

function pickCylindricalIndex(freeboard) {
  const target = freeboard * (2 / 3);
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < CYLINDRICAL.length; i++) {
    const dist = Math.abs(CYLINDRICAL[i].lengthCm - target);
    // On exact tie, prefer the larger model (later index).
    if (dist < bestDist || (dist === bestDist && i > bestIdx)) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function pickSphericalIndex(cylindricalIndex) {
  const raw = cylindricalIndex - 3;
  if (raw < 0) return 0;                          // clamp small-freeboard edge case
  if (raw > SPHERICAL.length - 1) return SPHERICAL.length - 1; // clamp C248
  return raw;
}

function recommend(boatLength, freeboard) {
  if (boatLength > MAX_BOAT_LENGTH_M) {
    return {
      status: 'bespoke',
      cylindrical: null,
      spherical: null,
      bespokeReason: 'Vessels over 30 m require a bespoke recommendation.',
    };
  }
  if (freeboard > MAX_FREEBOARD_CM) {
    return {
      status: 'bespoke',
      cylindrical: null,
      spherical: null,
      bespokeReason: 'Freeboard exceeds the largest standard fender; bespoke recommendation required.',
    };
  }

  const qty = quantityFromBoatLength(boatLength);
  const cIdx = pickCylindricalIndex(freeboard);
  const c = CYLINDRICAL[cIdx];

  const result = {
    status: 'ok',
    cylindrical: {
      model: c.model,
      diameterCm: c.diameterCm,
      lengthCm: c.lengthCm,
      qty: qty.cylindrical,
    },
    spherical: null,
    bespokeReason: null,
  };

  if (qty.spherical > 0) {
    const sIdx = pickSphericalIndex(cIdx);
    const s = SPHERICAL[sIdx];
    result.spherical = {
      model: s.model,
      dimsCm: s.dimsCm.slice(),
      qty: qty.spherical,
    };
  }

  return result;
}

const FenderEngine = { recommend, CYLINDRICAL, SPHERICAL };

if (typeof module !== 'undefined' && module.exports) {
  module.exports = FenderEngine;
}
if (typeof window !== 'undefined') {
  window.FenderEngine = FenderEngine;
}
